import "./navbar.css";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../utilities/FetchUtil";
import React, { FC, useEffect, useState } from "react";
import { tokenUsername } from "../utilities/TokenUtil";
import {
  AppShell,
  Burger,
  Button,
  Group,
  Menu,
  NavLink as MantineNavLink,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";

interface NavbarProps {
  children: React.ReactNode;
}

const Navbar: FC<NavbarProps> = ({ children }) => {
  const [opened, { toggle }] = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState(tokenUsername);

  useEffect(() => {
    let name = tokenUsername();
    setUsername(name);
  }, [username, location.pathname]);

  const handleSignOut = () => {
    logout().then(() => {
      localStorage.removeItem("jwtToken");
      navigate("/login");
    });
  };

  const theme = useMantineTheme();

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: "md",
        collapsed: { desktop: true, mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md">
          <Burger opened={opened} onClick={toggle} hiddenFrom="md" size="sm" />
          <Group justify="space-between" style={{ flex: 1 }}>
            <Text size="xl" fw={700}>
              Admin Dashboard
            </Text>
            <Group ml="xl" gap="lg" visibleFrom="md">
              <Menu trigger="hover">
                <Menu.Target>
                  <UnstyledButton>
                    <Group>
                      <Text size="xl">Interviews</Text>
                      <MdOutlineKeyboardArrowDown />
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Stack>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/active">
                        Active interviews
                      </UnstyledButton>
                    </Menu.Item>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/submitted">
                        Submitted interviews
                      </UnstyledButton>
                    </Menu.Item>
                  </Stack>
                </Menu.Dropdown>
              </Menu>
              <Menu trigger="hover">
                <Menu.Target>
                  <UnstyledButton>
                    <Group>
                      <Text size="xl">Chats</Text>
                      <MdOutlineKeyboardArrowDown />
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Stack>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/chat">
                        Chat sessions
                      </UnstyledButton>
                    </Menu.Item>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/display-name">
                        Change display name
                      </UnstyledButton>
                    </Menu.Item>
                  </Stack>
                </Menu.Dropdown>
              </Menu>
              {username === "superuser" && (
                <Menu trigger="hover">
                  <Menu.Target>
                    <UnstyledButton>
                      <Group>
                        <Text size="xl">Users</Text>
                        <MdOutlineKeyboardArrowDown />
                      </Group>
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Stack>
                      <Menu.Item>
                        <UnstyledButton component={NavLink} to="/register">
                          Register new user
                        </UnstyledButton>
                      </Menu.Item>
                      <Menu.Item>
                        <UnstyledButton
                          component={NavLink}
                          to="/change-password"
                        >
                          Change user's password
                        </UnstyledButton>
                      </Menu.Item>
                    </Stack>
                  </Menu.Dropdown>
                </Menu>
              )}

              <Menu trigger="hover">
                <Menu.Target>
                  <UnstyledButton>
                    <Group>
                      <Text size="xl">Tools & Insights</Text>
                      <MdOutlineKeyboardArrowDown />
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Stack>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/notifications">
                        Push notifications
                      </UnstyledButton>
                    </Menu.Item>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/analytics">
                        Analytic events
                      </UnstyledButton>
                    </Menu.Item>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/remote-configs">
                        Remote configs
                      </UnstyledButton>
                    </Menu.Item>
                    <Menu.Item>
                      <UnstyledButton component={NavLink} to="/manage-prompts">
                        Manage AI prompts
                      </UnstyledButton>
                    </Menu.Item>
                    <Menu.Item>
                      <UnstyledButton
                        component={NavLink}
                        to="/predefined-messages"
                      >
                        Manage predefined messages
                      </UnstyledButton>
                    </Menu.Item>
                  </Stack>
                </Menu.Dropdown>
              </Menu>
              <Text mx="sm" size="lg">
                {username && `Signed in as: ${username}`}
              </Text>
              <Button color={theme.colors.red[8]} onClick={handleSignOut}>
                <Text size="xl" c="white">
                  Sign out
                </Text>
              </Button>
            </Group>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar py="md" px={4}>
        <Stack h="100%" justify="space-between">
          <Stack gap={0}>
            <MantineNavLink label={<Text size="lg">Interviews</Text>}>
              <Stack gap={0}>
                <MantineNavLink
                  component={NavLink}
                  to="/active"
                  label={<Text size="lg">Active interviews</Text>}
                  onClick={() => {
                    toggle();
                  }}
                />
                <MantineNavLink
                  component={NavLink}
                  to="/submitted"
                  label={<Text size="lg">Submitted interviews</Text>}
                  onClick={() => {
                    toggle();
                  }}
                />
              </Stack>
            </MantineNavLink>
            <MantineNavLink label={<Text size="lg">Chats</Text>}>
              <Stack gap={0}>
                <MantineNavLink
                  component={NavLink}
                  to="/chat"
                  label={<Text size="lg">Chat sessions</Text>}
                  onClick={() => {
                    toggle();
                  }}
                />
                <MantineNavLink
                  component={NavLink}
                  to="/display-name"
                  label={<Text size="lg">Change display name</Text>}
                  onClick={() => {
                    toggle();
                  }}
                />
              </Stack>
            </MantineNavLink>
            {username === "superuser" && (
              <MantineNavLink label={<Text size="lg">Users</Text>}>
                <Stack gap={0}>
                  <MantineNavLink
                    component={NavLink}
                    to="/register"
                    label={<Text size="lg">Register new users</Text>}
                    onClick={() => {
                      toggle();
                    }}
                  />
                </Stack>
              </MantineNavLink>
            )}
            <MantineNavLink label={<Text size="lg">Tools & Insights</Text>}>
              <Stack gap={0}>
                <MantineNavLink
                  component={NavLink}
                  to="/manage-prompts"
                  label={<Text size="lg">Manage AI prompts</Text>}
                />
                <MantineNavLink
                  component={NavLink}
                  to="/analytics"
                  label={<Text size="lg">Analytic events</Text>}
                  onClick={() => {
                    toggle();
                  }}
                />
                <MantineNavLink
                  component={NavLink}
                  to="/notifications"
                  label={<Text size="lg">Push notifications</Text>}
                  onClick={() => {
                    toggle();
                  }}
                />
              </Stack>
            </MantineNavLink>
          </Stack>
          <Stack>
            <Text mx="sm" size="lg">
              Signed in as: {username}
            </Text>
            <Button m="sm" color={theme.colors.red[8]} onClick={handleSignOut}>
              Sign out
            </Button>
          </Stack>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
};

export default Navbar;
